<template>
    <div class="form-group" :class="model.size">
        <div>
            <label class="font-weight-bold" :for="'starRating' + _uid">{{ model.label }}</label>
            <i class="far fa-edit float-right text-primary pointer" @click="showEditPrompt"></i>
        </div>

        <div aria-describedby="descriptionHelp">
            <star-rating :id="'starRating' + _uid"
                         :name="'starRating' + _uid"
                         :star-size="30"
                         :max-rating="model.intMaxValue"
                         :show-rating="true"
                         :increment="1"
                         :round-start-rating="false"
                         :data-vv-as="model.label">
            </star-rating>
        </div>
        <small id="descriptionHelp" class="form-text text-muted">{{ isEnVersion ? model.descriptionEn : model.description }}</small>
        <span class="text-danger" v-if="errors.has('starRating' + _uid)">{{ errors.first('starRating' + _uid) }}</span>


        <vs-prompt :title="$t('editor.common.editComponentProperties')"
                   :accept-text="$t('buttons.ok')"
                   :cancel-text="$t('buttons.cancel')"
                   :active.sync="activePrompt"
                   :is-valid="validationSuccess"
                   @cancel="cancelPrompt"
                   @close="cancelPrompt"
                   color="primary">
            <div>
                <div v-if="model.locked">
                    <h4 class="text-danger"><i class="fas fa-lock"></i> {{ $t('registers.dynamic.gridHeaders.locked') }}</h4>
                </div>

                <FieldTemplateSelector v-model="model"
                                       :isFormField="isFormField"
                                       :isAuxRegisterField="isAuxRegisterField"
                                       :isEventFieldCode="isEventFieldCode">
                </FieldTemplateSelector>

                <vs-input :label="$t('editor.common.name')" v-model="model.name" />
                <span class="text-danger" v-if="!validName">{{ $t('validation.requiredField') }}</span>

                <div class="row mt-2 mb-2 pl-3">
                    <vs-checkbox v-model="model.isTemplate">{{ $t('editor.common.saveAsTemplate') }}</vs-checkbox>
                </div>

                <vs-input :label="$t('editor.common.nameEN')" v-model="model.nameEn"></vs-input>

                <vs-input :label="$t('editor.common.label')" v-model="model.label" />
                <span class="text-danger" v-if="!validLabel">{{ $t('validation.requiredField') }}</span>
                <vs-input :label="$t('editor.common.labelEn')" v-model="model.labelEn"></vs-input>
                <span class="text-danger" v-if="!validLabelEN">{{ $t('validation.requiredField') }}</span>

                <vs-input :label="$t('editor.common.description')" v-model="model.description" />
                <vs-input :label="$t('editor.common.descriptionEn')" v-model="model.descriptionEn" />

                <size-dropdown v-model="model.size"></size-dropdown>

                <div>
                    <vs-input-number :label="$t('editor.common.numberMinLength') + ':'" v-model="model.intMinValue" />
                    <span class="text-danger" v-if="!validIntMaxValue">{{ $t('validation.invalidIntNumber') }}</span>
                </div>
                <div>
                    <vs-input-number :label="$t('editor.common.numberMaxLength') + ':'" v-model="model.intMaxValue" />
                    <span class="text-danger" v-if="!validIntMaxValue">{{ $t('validation.invalidIntNumber') }}</span><br />
                    <span class="text-danger" v-if="!validMinMaxRange">{{ $t('validation.ivalidMinMaxValue') }}</span>
                </div>
                <hr>
                <div class="float-left mt-2">
                    <vs-checkbox v-model="model.isRequired">{{ $t('validation.requiredField') }}</vs-checkbox>
                </div>
                <div class="float-left mt-2">
                    <vs-checkbox v-model="model.isReadOnly">{{ $t('validation.readOnlyField') }}</vs-checkbox>
                </div>

                <div class="clearfix"></div>
                <hr />
                <div class="float-left mt-2">
                    <vs-checkbox v-model="model.showInDescription">{{ $t('editor.common.showInDescription') }}</vs-checkbox>
                </div>

                <div class="clearfix"></div>
            </div>
        </vs-prompt>
    </div>
</template>

<script>
    import { EditorComponentModel } from '../../models/models';
    import SizeDropdown from '../../components/editor/SizeDropdown';
    import FieldTemplateSelector from '../../components/editor/FieldTemplateSelector'
    import StarRating from 'vue-star-rating'
    import { Culture } from '../../models/enums';

    export default {
        inject: ['$validator'],
        props: {
            value: String,
            model: {
                type: EditorComponentModel
            },
            isFormField: {
                type: Boolean,
                default: false
            },
            isAuxRegisterField: {
                type: Boolean,
                required: false
            },
            isEventFieldCode: {
                type: Boolean,
                required: false
            },
        },
        components: {
            SizeDropdown,
            FieldTemplateSelector,
            StarRating
        },
        watch: {
            model: {
                handler: function (model) {
                    if (model.inputType === 'number') {
                        this.model.textAllowNullOrEmpty = this.originalModel.textAllowNullOrEmpty;
                    }


                },
                deep: true
            }
        },
        data() {
            return {
                activePrompt: false,
                validations: '',
                originalModel: new EditorComponentModel(),
                inputTypes: [{
                    text: this.$t('editor.input.typeText'),
                    value: 'text'
                }, {
                    text: this.$t('editor.input.typeNumber'),
                    value: 'number'
                }, {
                    text: this.$t('editor.input.typeTextarea'),
                    value: 'textarea'
                }],
                numberTypes: [{
                    text: this.$t('editor.input.intType'),
                    value: 'int'
                }, {
                    text: this.$t('editor.input.decimalType'),
                    value: 'decimal'
                }],
            }
        },
        mounted() {
            this.model.inputType = 'number';
            this.model.numberType = 'int';
        },
        methods: {
            showEditPrompt(event) {
                this.originalModel = new EditorComponentModel(this.model);
                this.activePrompt = true;
            },
            cancelPrompt() {
                this.changeModelToOriginalModel();
            },
            changeModelToOriginalModel() {
                this.model.name = this.originalModel.name;
                this.model.nameEn = this.originalModel.nameEn;
                this.model.label = this.originalModel.label;
                this.model.labelEn = this.originalModel.labelEn;
                this.model.size = this.originalModel.size;
                this.model.isRequired = this.originalModel.isRequired;
                this.model.isReadOnly = this.originalModel.isReadOnly;
                this.model.intMaxValue = this.originalModel.intMaxValue;
                this.model.showInDescription = this.originalModel.showInDescription;
            },
            inputTypeChanged() {
                this.resetTextEditorConstraintsValues();
            },
            numberTypeChanged() {
                this.resetTextEditorConstraintsValues();
            },
            resetTextEditorConstraintsValues() {
                this.model.textMinLength = this.originalModel.textMinLength;
                this.model.textMaxLength = this.originalModel.textMaxLength;
                this.model.textDefaultValue = this.originalModel.textDefaultValue;
                this.model.intMinValue = this.originalModel.intMinValue;
                this.model.intMaxValue = this.originalModel.intMaxValue;
                this.model.intDefaultValue = this.originalModel.intDefaultValue;
                this.model.decimalMinValue = this.originalModel.decimalMinValue;
                this.model.decimalMaxValue = this.originalModel.decimalMaxValue;
                this.model.decimalDefaultValue = this.originalModel.decimalDefaultValue;
                this.model.decimalPrecision = this.originalModel.decimalPrecision;
                this.model.numberFormat = this.originalModel.numberFormat;
            }
        },
        computed: {
            validName() {
                return this.model.name.length > 0;
            },
            validLabel() {
                return this.model.label.length > 0;
            },
            validLabelEN() {
                return this.model.labelEn.length > 0;
            },
            validIntMaxValue() {
                let num = parseInt(this.model.intMaxValue)
                return !isNaN(num) && num >= 0;
            },
            validMinMaxRange() {
                return this.model.intMinValue < this.model.intMaxValue;
            },
            validationSuccess() {
                // this.mode.locked - Locked for edition
                return !this.model.locked && this.validName && this.validLabel && this.validLabelEN && this.validIntMaxValue;
            },
            fieldTemplateSelected() {
                return !isNaN(this.fieldTemplateId) && this.fieldTemplateId > 0;
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
        }
    }
</script>

<style scoped>
    .vs-input {
        width: 100%;
    }

    ::v-deep {
        .vs-dialog {
            min-width: 800px !important;
            height: auto;
        }
    }
</style>
